<template>
  <v-card>
    <v-card-title class="headline">{{ $t('@terminationForm/title') }}</v-card-title>
    <errors-list :errorMessage="errorMessage" />

    <v-card-text>
      <v-select
        v-model="reason"
        color="green darken-1"
        :items="reasons"
        :label="$t('@terminationForm/reasonSelectLabel')"
        :loading="loading"
        required
      ></v-select>

      <v-textarea
        v-model="comment"
        color="green darken-1"
        auto-grow
        rows="1"
        :label="$t('@terminationForm/commentInputLabel')"
        name="comment"
      />

      <v-radio-group
        v-model="notificationType"
        row
        required
        :label="$t('@newContractForm/offerDeliveryRadioButtonsGroup/label')"
      >
        <v-radio
          :label="$t('@newContractForm/offerDeliveryRadioButtonsGroup/smsOptionLabel')"
          :value="notificationTypes.sms"
        ></v-radio>
        <v-radio
          :label="$t('@newContractForm/policyDeliveryRadioButtonsGroup/letterOptionLabel')"
          :value="notificationTypes.letter"
        ></v-radio>
      </v-radio-group>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="red darken-1" flat @click.stop="reset">
        {{ $t('@terminationForm/cancelButtonLabel') }}
      </v-btn>

      <v-btn color="green darken-1" :disabled="disabled" flat @click.stop="onSubmit">
        {{ $t('@terminationForm/submitButtonLabel') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { getErrorMessage } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';

const reasonMap = {
  expensive: 1,
  noNeed: 2,
  noLoan: 3,
  hasIncomeInsurance: 4,
  hasLoanProtection: 5,
  other: 6,
  unpaid: 7,
  notAccepted: 8,
  regret: 9,
};

const terminationNotificationTypeMap = {
  letter: 1,
  sms: 2,
  email: 3,
};

const terminationReasons = [
  {
    text: '@insurance/regretTerminationReasonLabel',
    value: 9,
  },
  {
    text: '@insurance/expensiveTerminationReasonLabel',
    value: 1,
  },
  {
    text: '@insurance/noNeedTerminationReasonLabel',
    value: 2,
  },
  {
    text: '@insurance/noLoanTerminationReasonLabel',
    value: 3,
  },
  {
    text: '@insurance/hasIncomeInsuranceTerminationReasonLabel',
    value: 4,
  },
  {
    text: '@insurance/hasLoanProtyectionTerminationReasonLabel',
    value: 5,
  },
  {
    text: '@insurance/unpaidTerminationReasonLabel',
    value: 7,
  },
  {
    text: '@insurance/otherTerminationReasonLabel',
    value: 6,
  },
];

const terminationReasonMap = {
  1: 'expensive',
  2: 'noNeed',
  3: 'noLoan',
  4: 'hasIncomeInsurance',
  5: 'hasLoanProtection',
  6: 'other',
  7: 'unpaid',
  8: 'notAccepted',
  9: 'regret',
  10: 'maxAgeReached',
};

export default {
  components: { ErrorsList },
  props: {
    contract: {
      type: Object,
    },
  },

  data() {
    return {
      reasons: [],
      reason: '',

      errorMessage: '',
      submitting: false,
      loading: false,

      comment: '',
      notificationType: terminationNotificationTypeMap.sms,
      notificationTypes: terminationNotificationTypeMap,
    };
  },

  computed: {
    disabled() {
      if (!this.reason || this.submitting) {
        return true;
      }
      if (!this.notificationType || this.submitting) {
        return true;
      }
      if (this.reason === reasonMap.other && !this.comment.length) {
        return true;
      }
      return false;
    },
  },

  methods: {
    reset() {
      this.$emit('dismiss');
      this.errorMessage = '';
      this.loading = false;
      this.submitting = false;
    },

    async getTerminationReasons() {
      if (!this.contract.id) {
        return;
      }

      try {
        this.loading = true;
        const {
          data: { reasons: reasons },
        } = await this.$http.get(`/api/insurances/${this.contract.id}/termination-reasons`);

        const reasonsToShow = [];
        reasons.map((a) => {
          const reason = terminationReasons.find((el) => el.value === reasonMap[a]);
          return reason && reasonsToShow.push({ ...reason, text: this.$t(reason.text) });
        });
        this.reasons = reasonsToShow;
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    async onSubmit() {
      const { reason, comment, notificationType } = this;

      try {
        if (!reason) {
          this.errorMessage = 'Field "Reason" is required';
          return;
        }
        if (!notificationType) {
          this.errorMessage = 'Field "Notification type" is required';
          return;
        }

        this.submitting = true;

        await this.$http.delete(
          `/api/insurances/${
            this.contract.id
          }?notificationType=${notificationType}&comment=${comment}&reason=${
            terminationReasonMap[reason]
          }`,
        );

        this.$emit('submitted');
        this.reset();
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.submitting = false;
      }
    },
  },

  mounted() {
    this.getTerminationReasons();
  },
};
</script>

<style scoped>
table.v-table tbody td.column-title {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.54) !important;
}
</style>
